import { cloneElement, useState } from 'react';
import { IconButton, List, ListItemButton, Popover, TableCell, Typography } from '@mui/material';

import isEmpty from 'Lib/utilities/isEmpty';
import stopPropagation from 'Lib/utilities/stopPropagation.js';

import TableRowDelete from './TableRowDelete';
import TableRowEdit from './TableRowEdit';

const TableButton = ({ action, button = false, data, label, onClick }) => {
    return button ? (
        <IconButton
            onClick={event => {
                event.stopPropagation();
                action.onClick(data);
            }}
        >
            {action.icon}
        </IconButton>
    ) : (
        <ListItemButton
            onClick={() => {
                onClick(data);
            }}
        >
            <Typography>{label}</Typography>
        </ListItemButton>
    );
};

const TableCellAction = ({ children, singleButton }) => {
    if (singleButton) {
        return (
            <TableCell
                onClick={event => {
                    stopPropagation(event);
                }}
            >
                {children({})}
            </TableCell>
        );
    } else {
        const [anchorElement, setAnchorElement] = useState(null);

        const handleClick = event => {
            /*
    			stopPropagation in both of `handleClick` & `handleClose` prevents the event from bubbling up
    			and navigating away from the table and into the View associated to that row
    		*/
            event.stopPropagation();
            setAnchorElement(event.currentTarget);
        };

        const handleClose = event => {
            event.stopPropagation();
            setAnchorElement(null);
        };

        const openPopover = Boolean(anchorElement);
        const id = openPopover ? 'actions-popover' : undefined;

        return (
            <TableCell
                sx={{ p: 0 }}
                onClick={event => {
                    stopPropagation(event);
                }}
            >
                <IconButton onClick={handleClick}>more_vert</IconButton>
                <Popover
                    id={id}
                    data-testid="actions-popover"
                    open={openPopover}
                    anchorEl={anchorElement}
                    onClose={handleClose}
                    anchorOrigin={{
                        vertical: 'center',
                        horizontal: 'left'
                    }}
                    transformOrigin={{
                        vertical: 'top',
                        horizontal: 'center'
                    }}
                >
                    <List>{children({ setAnchorElement })}</List>
                </Popover>
            </TableCell>
        );
    }
};

export default function TableActions({ actions, data, deletable, editable, columns, onDelete, onSubmit }) {
    const buttons = actions ? actions.map((action, index) => <TableButton key={`${action.label}_${index}`} {...action} data={data} columns={columns} />) : [];

    if (editable) buttons.push(<TableRowEdit data={data} columns={columns} onSubmit={onSubmit} />);
    if (deletable) buttons.push(<TableRowDelete data={data} onDelete={onDelete} />);

    if (isEmpty(buttons)) return null;
    else {
        const singleButton = buttons.length === 1 ? true : false;

        return (
            <TableCellAction
                singleButton={singleButton}
                onClick={event => {
                    stopPropagation(event);
                }}
            >
                {({ setAnchorElement }) =>
                    buttons.map((component, index) => {
                        return cloneElement(component, { key: component.key ?? index, button: singleButton, data, setAnchorElement });
                    })
                }
            </TableCellAction>
        );
    }
}
